import useChecklistCheckmark from "./useChecklistCheckmark.js";
import CheckItemMoreMenu from "./ChecklistItemMoreMenu.js";
import { type ChecklistResponse } from "@swivu/client-api";
import { format } from "date-fns/format";
import { QueryClient } from "@tanstack/react-query";
import Checkmark from "../Checkmark.js";
import ImageZoom from "../ImageZoom.js";
import { useChecklistContext } from "./ChecklistContext.js";
import { useState } from "react";
import { useIsGummy } from "../../hooks/useGummyTheme.js";
import { cn } from "../../utils/cn.js";

type CheckItemProps = {
  queryClient: QueryClient;
  totalItems: number;
  itemNumber: number;
  checklist: ChecklistResponse;
  handleChecked: (
    item: { checklist_item_id: number; status: 1 | 2 },
    e: PointerEvent
  ) => void;
  item: ChecklistResponse["items"][number];
  checkedItems: ChecklistResponse["items"];
};

const ChecklistItem = ({
  queryClient,
  item,
  totalItems,
  itemNumber,
  handleChecked,
  checklist,
  checkedItems
}: CheckItemProps) => {
  const { api } = useChecklistContext();
  const [imageCacheBuster, setImageCacheBuster] = useState(0);
  const incrementImageCacheBuster = () => {
    setImageCacheBuster((prev) => prev + 1);
  };
  const mutateObj = useChecklistCheckmark({
    handleChecked,
    item
  });
  const isGummy = useIsGummy();
  return (
    <div
      className={cn(
        "mobile-pref-card text-mono-800 dark:text-mono-200 relative px-4 py-4",
        isGummy
          ? cn("rounded-surface-md bg-white dark:bg-mono-900", "shadow-card")
          : "bg-card border-border rounded-md border shadow"
      )}
    >
      <div
        className={`mobile-pref-checklist-row grid gap-x-4 gap-y-6`}
        style={{ gridTemplateColumns: "auto 1fr" }}
      >
        <div
          className={`mobile-pref-checklist-status -mt-2 flex min-w-0 max-w-[100px] flex-col items-center`}
        >
          <Checkmark
            className="mobile-pref-checklist-checkmark"
            disabled={api === "public" || checklist.has_permission === false}
            showCheckmarkHint={
              api !== "public" && checkedItems.length === 0 && itemNumber === 1
            }
            isChecked={mutateObj.isChecked}
            handleChecked={mutateObj.handleMutate}
          />
          <p
            className={`mobile-pref-card-meta mobile-pref-checklist-count mobile-pref-checklist-count-status opacity-50`}
          >
            {itemNumber} of {totalItems}
          </p>
        </div>
        <div className={`mobile-pref-checklist-content flex-1`}>
          <div className={`space-y-4`}>
            <div className="pr-10">
              <h5
                className={cn(
                  "mobile-pref-card-title text-base/5 md:text-lg/5",
                  isGummy ? "font-extrabold" : "font-medium"
                )}
              >
                {item.description ?? "Check item"}
              </h5>
              {mutateObj.isChecked && item.checked_by && (
                <p
                  className={`mobile-pref-card-meta mt-1 leading-4 opacity-50`}
                >
                  Checked by {item.checked_by}
                  {item.checked_date
                    ? ` on ${format(new Date(item.checked_date), "MM/dd/yy")}`
                    : ``}
                </p>
              )}
              <p
                className={`mobile-pref-card-meta mobile-pref-checklist-count mobile-pref-checklist-count-inline opacity-50`}
              >
                {itemNumber} of {totalItems}
              </p>
            </div>
            {item.comment && (
              <div className={`mobile-pref-checklist-comment my-4`}>
                <p className="mobile-pref-card-body">{item.comment}</p>
                <p className={"mobile-pref-card-meta -mt-1 opacity-50"}>
                  {item.comment_by}
                  {item.comment_date
                    ? ` on ${format(new Date(item.comment_date), "MM/dd/yy")}`
                    : ``}
                </p>
              </div>
            )}
          </div>
        </div>

        <ChecklistImage item={item} imageCacheBuster={imageCacheBuster} />
      </div>
      {!!checklist.has_permission && (
        <div className="mobile-pref-checklist-menu absolute right-2 top-2">
          <CheckItemMoreMenu
            queryClient={queryClient}
            checklist_id={checklist.id}
            item={item}
            incrementImageCacheBuster={incrementImageCacheBuster}
          />
        </div>
      )}
    </div>
  );
};

const ChecklistImage = ({
  item,
  imageCacheBuster
}: {
  item: CheckItemProps["item"];
  imageCacheBuster: number;
}) => {
  if (!item.image_url && !item.thumbnail_url) {
    return null;
  }
  let thumbnailSrc = item.thumbnail_url ?? item.image_url ?? "";
  thumbnailSrc = thumbnailSrc.includes(`http`)
    ? thumbnailSrc
    : `localhost:8000/media/public${thumbnailSrc.replace(`/media`, ``)}`;

  let fullImageSrc = item.image_url ?? item.thumbnail_url ?? "";
  fullImageSrc = fullImageSrc.includes(`http`)
    ? fullImageSrc
    : `localhost:8000/media/public${fullImageSrc.replace(`/media`, ``)}`;

  return (
    <div className={`mobile-pref-checklist-image col-span-2`}>
      <ImageZoom src={`${fullImageSrc}?cacheBuster=${imageCacheBuster}`}>
        <img
          className="w-full max-w-xl rounded-surface-xs"
          alt={item.description ?? "checklist image"}
          src={`${thumbnailSrc}?cacheBuster=${imageCacheBuster}`}
        />
      </ImageZoom>
    </div>
  );
};

export default ChecklistItem;
